import { useMemo, useState } from "react";
import { RotateCcw, TrendingUp, Fuel, Droplet, Sun } from "lucide-react";

const defaults = {
  petrolPrice: 3100,
  cngPrice: 1550,
  distance: 2000,
  dailyDistance: 70,
  petrolConsumption: 12,
  cngConsumption: 14,
};

const AUTO_FACTOR = 1.4;
const DAYS_PER_MONTH = 30;

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Math.max(0, Math.round(n || 0))
  );

const fmtDec = (n) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(Math.max(0, n || 0));

export const Calculator = ({ t }) => {
  const [v, setV] = useState(defaults);
  const [cngAuto, setCngAuto] = useState(true);
  const [distanceMode, setDistanceMode] = useState("monthly"); // "daily" | "monthly"

  const update = (k) => (e) => {
    const raw = e.target.value;
    setV((prev) => ({ ...prev, [k]: raw === "" ? "" : Number(raw) }));
  };

  const reset = () => {
    setV(defaults);
    setCngAuto(true);
    setDistanceMode("monthly");
  };

  const effectiveCng = cngAuto
    ? (Number(v.petrolConsumption) || 0) * AUTO_FACTOR
    : Number(v.cngConsumption) || 0;

  const effectiveMonthlyDistance =
    distanceMode === "daily"
      ? (Number(v.dailyDistance) || 0) * DAYS_PER_MONTH
      : Number(v.distance) || 0;

  const { petrolCost, cngCost, daily, monthly, yearly } = useMemo(() => {
    const d = effectiveMonthlyDistance;
    const pp = Number(v.petrolPrice) || 0;
    const cp = Number(v.cngPrice) || 0;
    const pc = Number(v.petrolConsumption) || 1;
    const cc = effectiveCng || 1;

    const petrolLitres = d / pc;
    const cngKg = d / cc;
    const pcost = petrolLitres * pp;
    const ccost = cngKg * cp;
    const m = pcost - ccost;
    const dly = m / DAYS_PER_MONTH;
    const y = m * 12;
    return { petrolCost: pcost, cngCost: ccost, daily: dly, monthly: m, yearly: y };
  }, [v, effectiveCng, effectiveMonthlyDistance]);

  const savingsPct =
    petrolCost > 0 ? Math.max(0, Math.round(((petrolCost - cngCost) / petrolCost) * 100)) : 0;

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="bg-neutral-950 py-24 text-white md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-12 max-w-2xl">
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-green-400">
            {t.calculator.label}
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {t.calculator.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-neutral-400">
            {t.calculator.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-0 border border-neutral-800 lg:grid-cols-12">
          {/* Inputs */}
          <div className="border-neutral-800 p-8 md:p-10 lg:col-span-7 lg:border-r">
            <div className="mb-6 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
                Inputs
              </div>
              <button
                data-testid="calc-reset"
                onClick={reset}
                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-400 transition-colors hover:text-green-400"
              >
                <RotateCcw size={13} />
                {t.calculator.reset}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field
                id="petrol-price"
                label={t.calculator.petrolPrice}
                value={v.petrolPrice}
                onChange={update("petrolPrice")}
              />
              <Field
                id="cng-price"
                label={t.calculator.cngPrice}
                value={v.cngPrice}
                onChange={update("cngPrice")}
              />
              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="distance"
                    className="block text-[11px] uppercase tracking-[0.18em] text-neutral-400"
                  >
                    {distanceMode === "daily"
                      ? t.calculator.dailyDistance
                      : t.calculator.distance}
                  </label>
                  <div
                    data-testid="distance-mode-toggle"
                    className="flex overflow-hidden border border-neutral-700 text-[10px] font-semibold uppercase tracking-[0.12em]"
                  >
                    <button
                      type="button"
                      data-testid="distance-mode-daily"
                      onClick={() => setDistanceMode("daily")}
                      className={`px-2 py-1 transition-colors ${
                        distanceMode === "daily"
                          ? "bg-green-600 text-white"
                          : "bg-neutral-950 text-neutral-400 hover:text-white"
                      }`}
                    >
                      {t.calculator.daily}
                    </button>
                    <button
                      type="button"
                      data-testid="distance-mode-monthly"
                      onClick={() => setDistanceMode("monthly")}
                      className={`px-2 py-1 transition-colors ${
                        distanceMode === "monthly"
                          ? "bg-green-600 text-white"
                          : "bg-neutral-950 text-neutral-400 hover:text-white"
                      }`}
                    >
                      {t.calculator.monthly}
                    </button>
                  </div>
                </div>
                {distanceMode === "daily" ? (
                  <input
                    id="distance"
                    data-testid="calc-daily-distance"
                    type="number"
                    inputMode="decimal"
                    step="1"
                    min="0"
                    value={v.dailyDistance}
                    onChange={update("dailyDistance")}
                    className="mt-2 w-full border border-neutral-700 bg-neutral-950 px-3 py-3 text-base font-medium text-white outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
                  />
                ) : (
                  <input
                    id="distance"
                    data-testid="calc-distance"
                    type="number"
                    inputMode="decimal"
                    step="1"
                    min="0"
                    value={v.distance}
                    onChange={update("distance")}
                    className="mt-2 w-full border border-neutral-700 bg-neutral-950 px-3 py-3 text-base font-medium text-white outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
                  />
                )}
                {distanceMode === "daily" && (
                  <p className="mt-2 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    ≈ {fmt(effectiveMonthlyDistance)} km / {t.calculator.monthly.toLowerCase()}
                  </p>
                )}
              </div>
              <Field
                id="petrol-consumption"
                label={t.calculator.petrolConsumption}
                value={v.petrolConsumption}
                onChange={update("petrolConsumption")}
                step="0.1"
              />
              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="cng-consumption"
                    className="block text-[11px] uppercase tracking-[0.18em] text-neutral-400"
                  >
                    {t.calculator.cngConsumption}
                  </label>
                  <div
                    data-testid="cng-mode-toggle"
                    className="flex overflow-hidden border border-neutral-700 text-[10px] font-semibold uppercase tracking-[0.12em]"
                  >
                    <button
                      type="button"
                      data-testid="cng-mode-auto"
                      onClick={() => setCngAuto(true)}
                      className={`px-2 py-1 transition-colors ${
                        cngAuto
                          ? "bg-green-600 text-white"
                          : "bg-neutral-950 text-neutral-400 hover:text-white"
                      }`}
                    >
                      {t.calculator.autoMode}
                    </button>
                    <button
                      type="button"
                      data-testid="cng-mode-manual"
                      onClick={() => setCngAuto(false)}
                      className={`px-2 py-1 transition-colors ${
                        !cngAuto
                          ? "bg-green-600 text-white"
                          : "bg-neutral-950 text-neutral-400 hover:text-white"
                      }`}
                    >
                      {t.calculator.manualMode}
                    </button>
                  </div>
                </div>
                <input
                  id="cng-consumption"
                  data-testid="calc-cng-consumption"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={cngAuto ? fmtDec(effectiveCng) : v.cngConsumption}
                  onChange={update("cngConsumption")}
                  readOnly={cngAuto}
                  className={`mt-2 w-full border px-3 py-3 text-base font-medium outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/40 ${
                    cngAuto
                      ? "cursor-not-allowed border-neutral-800 bg-neutral-900 text-green-400"
                      : "border-neutral-700 bg-neutral-950 text-white"
                  }`}
                />
                {cngAuto && (
                  <p className="mt-2 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    {t.calculator.autoNote}
                  </p>
                )}
              </div>
            </div>

            <p className="mt-6 text-xs leading-relaxed text-neutral-500">
              {t.calculator.note}
            </p>
          </div>

          {/* Results */}
          <div className="bg-neutral-900 p-8 md:p-10 lg:col-span-5">
            <div className="mb-6 text-[11px] uppercase tracking-[0.25em] text-green-400">
              Results
            </div>

            <div className="space-y-5 border-b border-neutral-800 pb-6">
              <Row
                icon={<Fuel size={15} className="text-neutral-500" />}
                label={t.calculator.petrolCost}
                value={`TZS ${fmt(petrolCost)}`}
                muted
              />
              <Row
                icon={<Droplet size={15} className="text-green-400" />}
                label={t.calculator.cngCost}
                value={`TZS ${fmt(cngCost)}`}
              />
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-5">
                <div className="flex items-center gap-2">
                  <Sun size={15} className="text-green-400" />
                  <div className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                    {t.calculator.dailySavings}
                  </div>
                </div>
                <div
                  data-testid="daily-savings"
                  className="font-display text-xl font-bold tracking-tight text-white"
                >
                  TZS {fmt(daily)}
                </div>
              </div>

              <div className="mt-5 flex items-baseline justify-between">
                <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
                  {t.calculator.monthlySavings}
                </div>
                <div className="text-xs font-semibold text-green-400">
                  {savingsPct}%
                </div>
              </div>
              <div
                data-testid="monthly-savings"
                className="mt-2 font-display text-4xl font-black tracking-tighter text-green-400 sm:text-5xl"
              >
                TZS {fmt(monthly)}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-neutral-800 pt-6">
              <div className="flex items-center gap-2">
                <TrendingUp size={15} className="text-green-400" />
                <div className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                  {t.calculator.yearlySavings}
                </div>
              </div>
              <div
                data-testid="yearly-savings"
                className="font-display text-xl font-bold tracking-tight text-white"
              >
                TZS {fmt(yearly)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ id, label, value, onChange, step }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-[11px] uppercase tracking-[0.18em] text-neutral-400"
    >
      {label}
    </label>
    <input
      id={id}
      data-testid={`calc-${id}`}
      type="number"
      inputMode="decimal"
      step={step || "1"}
      min="0"
      value={value}
      onChange={onChange}
      className="mt-2 w-full border border-neutral-700 bg-neutral-950 px-3 py-3 text-base font-medium text-white outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
    />
  </div>
);

const Row = ({ icon, label, value, muted }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      {icon}
      <div className="text-xs uppercase tracking-[0.15em] text-neutral-400">
        {label}
      </div>
    </div>
    <div
      className={`font-display text-base font-bold tracking-tight ${
        muted ? "text-neutral-300" : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);
